import { isEmpty, partition } from 'lodash';

import {
  ROUTE_SORT_TYPE_BEST,
  ROUTE_SORT_TYPE_CO2,
  ROUTE_SORT_TYPE_DISTANCE,
  ROUTE_SORT_TYPE_DURATION,
  ROUTE_SORT_TYPE_FREQUENCY,
} from '@/lib/constants';
import { RouteModel } from '@/lib/models/route/types';
import { getTotalRouteModeEmissions } from '@/lib/models/route/utils/get-route-total-emissions';
import { SortSelectionInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';

export const filterRoutes = ({
  routes,
  filterBy,
  sortBy,
  originId,
  destinationId,
}: {
  routes: RouteModel[];
  filterBy: SegmentModeType[];
  sortBy: SortSelectionInterface;
  originId?: string;
  destinationId?: string;
}) => {
  // Filter routes by type
  const filteredRoutes = routes.filter((route) => filterBy.includes(route.primaryMode));
  // Sort Routes
  return sortRoutes(filteredRoutes, sortBy, originId, destinationId);
};

export const sortRoutes = (
  routes: RouteModel[],
  sortBy: SortSelectionInterface,
  originId?: string,
  destinationId?: string,
): RouteModel[] => {
  // Early out if 1 or less routes
  if (isEmpty(routes) || routes.length === 1) return routes;

  // partition visible and hidden routes while sorting
  const [hiddenRoutes, routesToSort] = partition(routes, 'hidden');

  // DISTANCE
  if (sortBy.sort === ROUTE_SORT_TYPE_DISTANCE) {
    // Sort by summed overall distance
    // Needs to be recalculated based on filtered options
    const sorted = sortByDistance(routesToSort);
    // Merge hidden routes back in
    return [...sorted, ...hiddenRoutes];
  }

  // DURATION
  if (sortBy.sort === ROUTE_SORT_TYPE_DURATION) {
    // Sort by summed overall duration
    // Needs to be recalculated based on filtered options
    const sorted = sortByDuration(routesToSort);
    // Merge hidden routes back in
    return [...sorted, ...hiddenRoutes];
  }

  // MOST FREQUENCY
  if (sortBy.sort === ROUTE_SORT_TYPE_FREQUENCY) {
    const sorted = routesToSort.sort((a, b) => {
      const aFrequency = getRouteFrequency(a);
      const bFrequency = getRouteFrequency(b);
      return bFrequency - aFrequency;
    });
    // Merge hidden routes back in
    return [...sorted, ...hiddenRoutes];
  }

  if (sortBy.sort === ROUTE_SORT_TYPE_BEST) {
    const sortedByScore = routesToSort.sort((a, b) => {
      const aLocationScore = getLocationMatchScore(a, originId, destinationId);
      const bLocationScore = getLocationMatchScore(b, originId, destinationId);
      const aIsDirect = getIsDirectRoute(a);
      const bIsDirect = getIsDirectRoute(b);

      // If the scores are equal, sort by target/source distance
      if (bLocationScore === aLocationScore) {
        const aDuration = a.durationSeconds;
        const bDuration = b.durationSeconds;
        const aDistance = (a.start?.distanceKm || 0) + (a.end?.distanceKm || 0);
        const bDistance = (b.start?.distanceKm || 0) + (b.end?.distanceKm || 0);
        const aFrequency = getRouteFrequency(a);
        const bFrequency = getRouteFrequency(b);
        const similarDistance = areDistancesSimilar(aDistance, bDistance);

        if (similarDistance) {
          const FREQUENCY_THRESHOLD = 2.5; // Needs to be 2.5x more frequent
          const DURATION_THRESHOLD = 1.2; // Needs to be 20% longer duration

          const higherFreq = Math.max(aFrequency, bFrequency);
          const lowerFreq = Math.min(aFrequency, bFrequency);
          const frequencyRatio = higherFreq / lowerFreq;

          const shorterDuration = Math.min(aDuration, bDuration);
          const longerDuration = Math.max(aDuration, bDuration);
          const durationRatio = longerDuration / shorterDuration;

          // Check if frequency difference is significant
          const hasSignificantFrequencyDiff = frequencyRatio >= FREQUENCY_THRESHOLD;

          // Check if duration difference is significant
          const hasSignificantDurationDiff = durationRatio >= DURATION_THRESHOLD;

          if (hasSignificantFrequencyDiff && hasSignificantDurationDiff) {
            // Both frequency and duration are significantly different
            // Create a combined score that weighs both factors
            const aScore = (aFrequency / bFrequency) * (bDuration / aDuration); // Higher frequency * lower duration = better
            const bScore = (bFrequency / aFrequency) * (aDuration / bDuration);

            return bScore - aScore; // Higher combined score wins
          }
          if (hasSignificantFrequencyDiff) {
            // Only frequency is significantly different, prefer higher frequency
            return bFrequency - aFrequency;
          }

          if (hasSignificantDurationDiff) {
            // Only duration is significantly different, prefer shorter duration
            return aDuration - bDuration;
          }
        }

        const aDistanceWithPenalty = !aIsDirect ? aDistance * 2 : aDistance;
        const bDistanceWithPenalty = !bIsDirect ? bDistance * 2 : bDistance;

        return aDistanceWithPenalty - bDistanceWithPenalty;
      }
      // Else sort by highest score
      return bLocationScore - aLocationScore;
    });

    const [bestMatch, ...remaining] = sortedByScore;
    const sortedByDuration = sortByDuration(remaining);

    return [bestMatch, ...sortedByDuration];
  }

  if (sortBy.sort === ROUTE_SORT_TYPE_CO2) {
    const sorted = routesToSort.sort((a, b) => {
      const aEmissions = getTotalRouteModeEmissions([a.mainOptions[0].emissions, a.start?.emissions, a.end?.emissions]);
      const bEmissions = getTotalRouteModeEmissions([b.mainOptions[0].emissions, b.start?.emissions, b.end?.emissions]);
      return aEmissions.total - bEmissions.total;
    });
    // Merge hidden routes back in
    return [...sorted, ...hiddenRoutes];
  }

  // else no sort applied, return everything
  return routes;
};

const sortByDuration = (routes: RouteModel[]): RouteModel[] => {
  return routes.sort((a, b) => {
    const aDuration = a.durationSeconds;
    const bDuration = b.durationSeconds;
    return aDuration - bDuration;
  });
};

const sortByDistance = (routes: RouteModel[]): RouteModel[] => {
  return routes.sort((a, b) => {
    return a.distanceKm - b.distanceKm;
  });
};

const getRouteFrequency = (route: RouteModel): number => {
  return route.frequency || 0;
};

const getIsDirectRoute = (route: RouteModel): boolean => {
  return route.mainOptions[0].segments.length === 1;
};

const getLocationMatchScore = (route: RouteModel, locationA?: string, locationB?: string): number => {
  let score = 0;

  if (locationA && route.fromId.includes(locationA)) {
    score += 1;
  }
  if (locationB && route.toId.includes(locationB)) {
    score += 1;
  }

  return score;
};

const areDistancesSimilar = (aDistance: number, bDistance: number): boolean => {
  const avgDistance = (aDistance + bDistance) / 2;
  const tolerance = Math.log10(avgDistance + 1) * 30; // Adjust multiplier as needed
  const difference = Math.abs(aDistance - bDistance);

  return difference <= tolerance;
};
